/**
 * SkillDelivery Platform
 * WorkspaceHeader Component - Week 1-2: Workspace Shell
 */

import React, { useState } from 'react';
import { Linkedin, Edit2, Check, X, Building2, GraduationCap, Award, User } from 'lucide-react';
import './WorkspaceHeader.css';

const WorkspaceHeader = ({ profile, onLinkedInUpdate }) => {
  const [linkedinUrl, setLinkedinUrl] = useState(profile?.linkedinUrl || '');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const accountTypeConfig = {
    individual: {
      label: 'Individual',
      icon: User,
      color: '#4F46E5',
      bgColor: 'rgba(79, 70, 229, 0.1)'
    },
    organization: {
      label: 'Organization',
      icon: Building2,
      color: '#059669',
      bgColor: 'rgba(5, 150, 105, 0.1)'
    },
    university: {
      label: 'University',
      icon: GraduationCap,
      color: '#7C3AED',
      bgColor: 'rgba(124, 58, 237, 0.1)'
    },
    institute: {
      label: 'Institute',
      icon: Award,
      color: '#DC2626',
      bgColor: 'rgba(220, 38, 38, 0.1)'
    }
  };

  const currentConfig = accountTypeConfig[profile?.accountType] || accountTypeConfig.individual;
  const IconComponent = currentConfig.icon;

  const handleSaveLinkedIn = async () => {
    if (!linkedinUrl && !profile?.linkedinUrl) {
      setIsEditing(false);
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/workspace/profile', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ linkedinUrl })
      });

      if (response.ok) {
        onLinkedInUpdate && onLinkedInUpdate(linkedinUrl);
        setIsEditing(false);
      }
    } catch (error) {
      console.error('Failed to save LinkedIn URL:', error);
    } finally {
      setSaving(false);
    }
  };

  const completionPct = profile?.profileCompletedPct || 0;

  const getCompletionColor = (pct) => {
    if (pct >= 80) return '#059669';
    if (pct >= 50) return '#F59E0B';
    return '#DC2626';
  };

  return (
    <div className="workspace-header">
      <div className="workspace-header-left">
        {/* Account Type Badge */}
        <div
          className="account-type-badge"
          style={{
            backgroundColor: currentConfig.bgColor,
            color: currentConfig.color,
            border: `1px solid ${currentConfig.color}`
          }}
        >
          <IconComponent size={16} />
          <span>{currentConfig.label}</span>
        </div>

        {/* User Info */}
        <div className="workspace-user-info">
          <h2 className="workspace-user-name">{profile?.name || 'Welcome'}</h2>
          {profile?.vertical && (
            <span className="workspace-vertical-badge">
              {profile.vertical}
            </span>
          )}
        </div>
      </div>

      <div className="workspace-header-center">
        {/* Profile Completion */}
        <div className="profile-completion">
          <div className="profile-completion-label">
            Profile Completion: <strong>{completionPct}%</strong>
          </div>
          <div className="profile-completion-bar">
            <div
              className="profile-completion-fill"
              style={{
                width: `${completionPct}%`,
                backgroundColor: getCompletionColor(completionPct)
              }}
            />
          </div>
        </div>
      </div>

      <div className="workspace-header-right">
        {/* LinkedIn URL */}
        <div className="linkedin-section">
          {isEditing ? (
            <div className="linkedin-edit">
              <input
                type="url"
                placeholder="https://linkedin.com/in/yourprofile"
                value={linkedinUrl}
                onChange={(e) => setLinkedinUrl(e.target.value)}
                className="linkedin-input"
              />
              <button
                onClick={handleSaveLinkedIn}
                disabled={saving}
                className="linkedin-save-btn"
                title="Save"
              >
                <Check size={16} />
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="linkedin-cancel-btn"
                title="Cancel"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="linkedin-display">
              {profile?.linkedinUrl ? (
                <a
                  href={profile.linkedinUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="linkedin-link"
                >
                  <Linkedin size={18} />
                  <span>LinkedIn</span>
                </a>
              ) : (
                <span className="linkedin-not-set">
                  <Linkedin size={18} />
                  <span>Add LinkedIn</span>
                </span>
              )}
              <button
                onClick={() => setIsEditing(true)}
                className="linkedin-edit-btn"
                title={profile?.linkedinUrl ? 'Edit LinkedIn' : 'Add LinkedIn'}
              >
                <Edit2 size={14} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceHeader;
